"use client";

import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { useGeolocation } from "@/hooks/useGeolocation";
import { usePendingChangesDetection } from "@/hooks/usePendingChangesDetection";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";
import { useLocationGeolocationFlow } from "@/hooks/useLocationGeolocationFlow";
import { usePendingSearch } from "@/hooks/usePendingSearch";
import { trackFirstDiscoverInteraction } from "@/lib/analytics/discoverInteractions";
import { useTranslations } from "next-intl";
import pendingSearch from "@/lib/pendingSearch";
import {
  parseSelection,
  withRouteActivity,
} from "@/components/discover/filters/selection";
import type {
  LocationOption,
  PlacePrediction,
} from "@/types/location-selector";
import {
  getAreaCountry,
  getAreaIdsByCountry,
  mapAreaCoords,
  type CustomLocation,
  type MapAreaId,
} from "@/utils/location";
import { activitySlugToKey } from "@/utils/slugs";
import { useParams, useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";

export type UseLocationSelectorArgs = {
  currentArea: MapAreaId;
  /** Optional custom location from URL params */
  currentCustomLocation?: CustomLocation | null;
  /** Called after selection completes - component should close the selector */
  onClose?: () => void;
};

export function useLocationSelector({
  currentArea,
  currentCustomLocation,
  onClose,
}: UseLocationSelectorArgs) {
  const { setTriggerOpen, setHasPendingChanges } = useSearchTriggerContext();
  const searchParams = useSearchParams();
  const { activity } = useParams<{ activity?: string }>();
  const t = useTranslations();

  // Get current activities from the URL, overridden by the route's activity
  // segment when present (route wins, mirroring the server's merge).
  const currentActivities = withRouteActivity(
    parseSelection(searchParams),
    activitySlugToKey(activity),
  ).activities;

  const { hasPendingChanges } = usePendingChangesDetection({
    currentArea,
    currentActivities,
    currentCustomLocation,
  });

  // Places autocomplete
  const {
    inputValue: searchQuery,
    setInputValue: setSearchQuery,
    suggestions: placeSuggestions,
    isLoading: isSearching,
    isDebouncing,
    clear: clearSearch,
    getPlaceDetails,
    isLoadingDetails,
  } = usePlacesAutocomplete({ debounceMs: 300, minChars: 2 });

  // Reverse geocoding (separate hook for single responsibility)
  const { reverseGeocode, isLoading: isReverseGeocoding } = useReverseGeocode();

  // Geolocation
  const {
    position: geoPosition,
    error: geoError,
    isLoading: isGeoLoading,
    requestLocation,
    isSupported: isGeoSupported,
  } = useGeolocation();

  // Pending state. Subscribing here is what keeps the label in step with a
  // write that happened in the other mounted search bar.
  const pendingState = usePendingSearch();
  const pendingAreaParam = pendingState.area;
  const pendingCustomLocation = pendingState.customLocation;

  const validPendingArea =
    pendingAreaParam && pendingAreaParam in mapAreaCoords
      ? (pendingAreaParam as MapAreaId)
      : undefined;

  // Determine effective current location display
  // If there's a pending area, it takes precedence (clears custom location)
  // If there's a pending custom location, it takes precedence over current area
  const effectiveCustomLocation = validPendingArea
    ? null // Predefined area selected - no custom location
    : (pendingCustomLocation ?? currentCustomLocation);
  const effectiveCurrentArea = validPendingArea ?? currentArea;

  // Get the country of the current area to filter suggested cities
  const currentCountry = getAreaCountry(effectiveCurrentArea);

  // Get area IDs for the current country
  const currentCountryAreaIds = useMemo(
    () => getAreaIdsByCountry(currentCountry),
    [currentCountry],
  );

  // Build location options for the current country only
  const currentCountryOptions: LocationOption[] = useMemo(
    () =>
      currentCountryAreaIds.map((key) => ({
        value: key,
        label: t(`mapAreas.${key}.city`),
        description: t(`mapAreas.${key}.description`),
        country: t(`mapAreas.${key}.country`),
      })),
    [currentCountryAreaIds, t],
  );

  const effectiveLabel = effectiveCustomLocation
    ? effectiveCustomLocation.displayName || t("locationSelector.yourLocation")
    : t(`mapAreas.${effectiveCurrentArea}.city`);

  const currentLabel = currentCustomLocation
    ? currentCustomLocation.displayName || t("locationSelector.yourLocation")
    : t(`mapAreas.${currentArea}.city`);

  // Called when the selector closes - handles cleanup and pending state
  const handleClose = useCallback(() => {
    const hasChanges = hasPendingChanges();
    setHasPendingChanges(hasChanges);
    if (!hasChanges) {
      setTriggerOpen("location-selector", false);
    }
    clearSearch();
    onClose?.();
  }, [
    setTriggerOpen,
    setHasPendingChanges,
    hasPendingChanges,
    clearSearch,
    onClose,
  ]);

  const handleAreaSelect = useCallback(
    (newArea: MapAreaId) => {
      trackFirstDiscoverInteraction("location_selector");
      // Clear search query immediately
      clearSearch();
      // Set the new area (this also clears any custom location)
      pendingSearch.setArea(newArea);

      // Mark that we have pending changes
      const hasChanges =
        newArea !== currentArea || currentCustomLocation != null;
      setHasPendingChanges(hasChanges);
      // Close the selector
      onClose?.();
    },
    [
      currentArea,
      currentCustomLocation,
      setHasPendingChanges,
      clearSearch,
      onClose,
    ],
  );

  const handleCustomLocationSelect = useCallback(
    (location: CustomLocation) => {
      pendingSearch.setCustomLocation(location);
      setHasPendingChanges(true);
      // Close the selector
      onClose?.();
    },
    [setHasPendingChanges, onClose],
  );

  const handlePlaceSelect = useCallback(
    async (prediction: PlacePrediction) => {
      trackFirstDiscoverInteraction("location_selector");
      const details = await getPlaceDetails(prediction.placeId);
      if (details) {
        handleCustomLocationSelect({
          lat: details.lat,
          lon: details.lon,
          displayName: prediction.mainText,
          secondaryText: prediction.secondaryText,
        });
      }
    },
    [getPlaceDetails, handleCustomLocationSelect],
  );

  const { handleUseMyLocation: requestMyLocation } = useLocationGeolocationFlow(
    {
      geoPosition,
      geoError,
      isGeoLoading,
      requestLocation,
      reverseGeocode,
      onCustomLocationResolved: handleCustomLocationSelect,
    },
  );

  // Counted on the click, not when the position comes back.
  const handleUseMyLocation = useCallback(() => {
    trackFirstDiscoverInteraction("location_selector");
    requestMyLocation();
  }, [requestMyLocation]);

  return {
    // Location options (filtered by current country)
    currentCountryOptions,

    // Current/effective state
    currentArea,
    currentLabel,
    currentCustomLocation,
    effectiveCurrentArea,
    effectiveLabel,
    effectiveCustomLocation,

    // Actions (pure - component controls open/close)
    handleAreaSelect,
    handlePlaceSelect,
    handleUseMyLocation,
    handleClose,

    // Search state
    searchQuery,
    setSearchQuery,
    placeSuggestions,
    isSearching,
    isDebouncing,
    clearSearch,
    isLoadingDetails,
    isReverseGeocoding,

    // Geolocation state
    geoPosition,
    geoError,
    isGeoLoading,
    isGeoSupported,
  } as const;
}
